import { useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from '../../components/ConfirmationModal.jsx'
import DebugAccordion from '../../components/DebugAccordion'
import JsonForm from '../../components/JsonForm.jsx'
import Loadable from '../../components/Loadable.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import UtgNav from './components/UtgNav'
import UtgTools from './components/UtgTools.jsx'

import { fetchComponentConfig, updateComponentConfig } from '../../state/actions/componentConfigActions.js'
import { fetchComponentConfigSchema } from '../../state/actions/componentConfigSchemaActions'
import { NotificationTypes, addNotification } from '../../state/actions/notificationActions'
import { fetchUtg } from '../../state/actions/utgActions.js'

import { componentConfigSignal, componentConfigLoadedSignal, componentConfigErrorSignal } from '../../state/signals/componentConfigSignals.js'
import { componentConfigSchemaSignal, componentConfigSchemaLoadedSignal, componentConfigSchemaErrorSignal } from '../../state/signals/componentConfigSchemaSignals'
import { utgSignal, utgLoadedSignal, utgErrorSignal } from '../../state/signals/utgSignals.js'

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'
import { isEmptyObject, makeFieldsReadonly } from '../../utils/utils'
export default function UtgConfig() {

    const navigate = useNavigate()
    const { beId, componentId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/utgs`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewUtgs) {
            addNotification('You are not authorized to view UTGs for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(
        () => {
            return userPermissionsSummary?.value?.canEditUtgs
        }
    )

    useEffect(() => {
        if (beIdChanged) return
        fetchUtg(beId, componentId, config, accessToken).then(
            () => {
                fetchComponentConfigSchema(beId, utgSignal.value.utgVersion, config, accessToken)
                fetchComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, config, accessToken)
            }
        )
    }, [beId, beIdChanged, componentId, config, accessToken])

    const jsonFormData = useComputed(() => {
        if (!componentConfigLoadedSignal.value || !componentConfigSchemaLoadedSignal.value) {
            return null
        }
        if (!isEmptyObject(componentConfigSchemaSignal.value) && !isEmptyObject(componentConfigSignal.value)) {

            // deep clone the schema
            let clonedSchema = structuredClone(componentConfigSchemaSignal.value)

            // remove the api interfaces from the schema since those are edited in a separate page
            delete clonedSchema.properties.config.properties.apiInterfaceConfig

            // disable editing of all fields if canEditComputed is false
            if (!userPermissionsSummary?.value?.canEditUtgs) {
                clonedSchema = makeFieldsReadonly(clonedSchema)
            }

            // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
            globalThis.SCHEMA_MODE = 'CONFIG'
            console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)

            // NOTE: if onSubmitValid is changed to onSubmit then the callback must also be changed to receive both errors and values
            const formData = {
                schema: clonedSchema.properties.config,
                value: componentConfigSignal.value.config,
                loadedComponentConfig: componentConfigSignal.value.config,
                onSubmitValid: handleFormSubmit,
                onCancel: handleCancel,
                onFormChange: handleFormChange,
                canEditConfig: userPermissionsSummary?.value?.canEditUtgs
            }
            console.log('formData from config', formData)
            return formData
        }
    })

    const showCancelModalSignal = useSignal(false)
    const isFormEditedSignal = useSignal(false)
    const accordionData = [
        { name: 'Component Config Data', data: componentConfigSignal.value },
        { name: 'UTG Data', data: utgSignal.value },
        { name: 'Component Schema', data: componentConfigSchemaSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    const handleFormSubmit = values => {
        if (!userPermissionsSummary?.value?.canEditUtgs) {
            addNotification('You are not authorized to edit this UTG.', NotificationTypes.ERROR)
            return false
        }
        try {
            console.log('values', values)
            componentConfigLoadedSignal.value = false
            if (values) {

                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    config: values,
                    hash: componentConfigSignal.value.hash,
                    isCommitted: componentConfigSignal.value.isCommitted,
                    lastUpdatedAt: componentConfigSignal.value.lastUpdatedAt,
                    lastUpdatedBy: componentConfigSignal.value.lastUpdatedBy,
                    revisionId: componentConfigSignal.value.revisionId,
                    componentId: componentConfigSignal.value.componentId
                }
                data.config.apiInterfaceConfig = componentConfigSignal.value.config.apiInterfaceConfig || {}
                data.config.apiInterfaceConfig.apiInterface = data.config.apiInterfaceConfig.apiInterface || []
                data.config.deviceConfig = componentConfigSignal.value.config.deviceConfig || {}
                data.config.deviceConfig.device = data.config.deviceConfig.device || []

                //console.log(data)
                updateComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, data, config, accessToken, true)
                    .then(result => {
                        addNotification('Component Config was successfully updated.', NotificationTypes.SUCCESS, true)
                        navigate(`/be/${beId}/utgs/${componentId}`)
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification(`Unable to update the Component Config. ${error.toString()}`, NotificationTypes.ERROR, false)
                        return false
                    })
                    .finally(() => {
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEditedSignal.value) {
            showCancelModalSignal.value = true
        }
        else {
            navigate('../')
        }
    }

    const handleFormChange = () => {
        isFormEditedSignal.value = true
    }

    return (
        <>
            <ConfirmationModal show={showCancelModalSignal.value} handleCancel={() => showCancelModalSignal.value = false} handleConfirm={() => navigate('../')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                    UTG List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    utgLoadedSignal.value ? <>Manage {utgSignal.value.componentNickname}</> : <>Loading...</>
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title='UTG Configuration' canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[componentConfigLoadedSignal]} errorSignals={[componentConfigErrorSignal]}>
                    <UtgTools componentConfig={componentConfigSignal.value}></UtgTools>
                </Loadable>
            </Stack>
            <UtgNav activeKey="4"></UtgNav>
            <Loadable loadedSignals={[utgLoadedSignal, componentConfigSchemaLoadedSignal, componentConfigLoadedSignal]} errorSignals={[utgErrorSignal, componentConfigSchemaErrorSignal, componentConfigErrorSignal]}>
                <JsonForm data={jsonFormData.value}></JsonForm>
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}